import { useEffect, useMemo, useRef, useState } from "react";
import {
    ChevronDownIcon,
    ClipboardDocumentListIcon,
    CubeIcon,
    PlusIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import Drawer from "../Drawer";
import { AddDrawerSelect } from "../productCatalog/AddDrawerSelect";
import { ACTIVE, enumInventoryStatuses } from "../../consts/inventoryStatus";
import { arrayHasItems } from "../../helpers/common";
import { LoaderSpinner } from "../LoaderSpinner";
import SupplySku from "../SupplySku";
import CopyBomPicker from "./CopyBomPicker";

const PRODUCT_BOM_SUPPLIER_STACK = (lineId) => `edit-product-bom-supplier-${lineId}`;

/** Stacking keys for AddDrawerSelect (same pattern as ProductCatalog add drawer). */
const EDIT_BOM_STACK = {
    status: "edit-bom-status",
    category: "edit-bom-category",
    subcategory: "edit-bom-subcategory",
    primaryVendor: "edit-bom-primary-vendor",
};

const CATALOG_STATUS_OPTIONS = [
    { value: "ACTIVE", label: "Active" },
    { value: "IN_ACTIVE", label: "Inactive" },
    { value: "SUNSET", label: "Sunset" },
    { value: "PLANNED", label: "Planned" },
];

function labelToInventoryStatusCode(displayLabel) {
    const entry = Object.entries(enumInventoryStatuses).find(([, label]) => label === displayLabel);
    return entry ? entry[0] : ACTIVE;
}

const fmtCost = (n) =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    }).format(Number(n || 0));

function blankLine() {
    return {
        id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        componentName: "",
        componentType: "",
        description: "",
        supplier: "",
        vendorId: "",
        packagingGroupId: "",
        componentPresetId: "",
        qty: 1,
        unitCost: 0,
        extendedCost: 0,
        isNewComponent: false,
    };
}

function mapComponentToLine(c, index) {
    const qty = Number(c.qty ?? 1);
    const unitCost = Number(c.unitCost ?? 0);
    const vendorId =
        c.vendorId != null && c.vendorId !== ""
            ? String(c.vendorId)
            : c.vendor_id != null && c.vendor_id !== ""
              ? String(c.vendor_id)
              : "";
    return {
        id: c.id || `line-${Date.now()}-${index}`,
        componentName: String(c.componentName || c.component || "").trim(),
        componentType: String(c.componentType || "").trim(),
        description: String(c.description || "").trim(),
        supplier: String(c.supplier || "").trim(),
        vendorId,
        packagingGroupId:
            c.packagingGroupId != null && c.packagingGroupId !== ""
                ? String(c.packagingGroupId)
                : c.packaging_group_id != null && c.packaging_group_id !== ""
                  ? String(c.packaging_group_id)
                  : "",
        componentPresetId:
            c.componentPresetId != null && c.componentPresetId !== ""
                ? String(c.componentPresetId)
                : c.component_preset_id != null && c.component_preset_id !== ""
                  ? String(c.component_preset_id)
                  : "",
        qty,
        unitCost,
        extendedCost: Math.round(qty * unitCost * 10000) / 10000,
        isNewComponent: false,
    };
}

/**
 * Edit BOM — right drawer matching EditBomSheet layout (tabs, product fields, component cards).
 * Separate from add flow (ProductBomDrawer).
 */
export default function EditProductBomDrawer({
    isOpen,
    onClose,
    product,
    categorySettingsItems = [],
    componentLibrary = [],
    boxPresets = [],
    vendorOptions = [],
    vendorSelectLoading = false,
    onSave,
    saveBusy = false,
    productBomRows = [],
}) {
    const [activeSection, setActiveSection] = useState("components");
    const [productName, setProductName] = useState("");
    const [variantName, setVariantName] = useState("");
    const [statusCode, setStatusCode] = useState(ACTIVE);
    const [categoryGroupId, setCategoryGroupId] = useState("");
    const [subcategoryVariantId, setSubcategoryVariantId] = useState("");
    const [productVendorId, setProductVendorId] = useState("");
    const [leadTime, setLeadTime] = useState("");
    const [lines, setLines] = useState([blankLine()]);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [dropdownSearch, setDropdownSearch] = useState("");
    const [openDrawerSelectKey, setOpenDrawerSelectKey] = useState(null);
    const [copySourceId, setCopySourceId] = useState("");
    const dropdownRef = useRef(null);

    const categoryOptions = useMemo(
        () =>
            (categorySettingsItems || []).map((g) => ({
                value: String(g.id),
                label: g.name || String(g.id),
            })),
        [categorySettingsItems]
    );

    const subcategoryOptions = useMemo(() => {
        if (!categoryGroupId) return [];
        const group = (categorySettingsItems || []).find((g) => String(g.id) === String(categoryGroupId));
        return (group?.variants || []).map((v) => ({
            value: String(v.id),
            label: v.name || String(v.id),
        }));
    }, [categorySettingsItems, categoryGroupId]);

    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpenDropdown(null);
                setDropdownSearch("");
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        if (!isOpen || !product) return;
        const pName = String(product.canonicalName || product.productName || "").trim();
        setProductName(pName);
        setVariantName(String(product.variantName || "").trim());
        setStatusCode(
            String(product.inventoryStatusCode || labelToInventoryStatusCode(product.status) || ACTIVE)
        );
        setCategoryGroupId(
            product.category_group_id != null && product.category_group_id !== ""
                ? String(product.category_group_id)
                : ""
        );
        setSubcategoryVariantId(
            product.subcategory_variant_id != null && product.subcategory_variant_id !== ""
                ? String(product.subcategory_variant_id)
                : ""
        );
        setProductVendorId(product.vendor_id != null && product.vendor_id !== "" ? String(product.vendor_id) : "");
        setLeadTime(
            product.leadTimeDays != null && product.leadTimeDays !== ""
                ? String(product.leadTimeDays)
                : product.leadTime != null
                  ? String(product.leadTime)
                  : ""
        );
        const comps = (product.components || []).map(mapComponentToLine);
        setLines(comps.length ? comps : [blankLine()]);
        setOpenDropdown(null);
        setDropdownSearch("");
        setOpenDrawerSelectKey(null);
        setActiveSection("components");
        setCopySourceId("");
    }, [isOpen, product]);

    const handleCopySource = (source) => {
        setCopySourceId(String(source?.id || ""));
        setLines(
            (source?.components || []).map((component, index) => ({
                ...mapComponentToLine(component, index),
                id: `copied-line-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
            }))
        );
    };

    const filteredLibrary = useMemo(() => {
        const q = dropdownSearch.trim().toLowerCase();
        const lib = componentLibrary || [];
        if (!q) return lib.slice(0, 25);
        return lib
            .filter((c) => {
                const hay = [c.name, c.type, c.notes, c.defaultSupplier].filter(Boolean).join(" ").toLowerCase();
                return hay.includes(q);
            })
            .slice(0, 25);
    }, [componentLibrary, dropdownSearch]);

    const filteredBoxPresets = useMemo(() => {
        const q = dropdownSearch.trim().toLowerCase();
        const presets = boxPresets || [];
        if (!q) return presets.slice(0, 15);
        return presets
            .filter((p) => {
                const hay = [p.name, p.notes, ...(p.components || []).map((c) => c.component)]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return hay.includes(q);
            })
            .slice(0, 15);
    }, [boxPresets, dropdownSearch]);

    const dropdownSearchMatchesLibrary =
        dropdownSearch.trim().length > 0 &&
        (componentLibrary || []).some((c) => String(c.name || "").toLowerCase() === dropdownSearch.trim().toLowerCase());

    const updateLine = (id, patch) => {
        setLines((prev) =>
            prev.map((l) => {
                if (l.id !== id) return l;
                const next = { ...l, ...patch };
                next.extendedCost = Math.round(Number(next.qty || 0) * Number(next.unitCost || 0) * 10000) / 10000;
                return next;
            })
        );
    };

    const selectLibraryComponent = (lineId, comp) => {
        const vid =
            comp.vendorId != null && comp.vendorId !== ""
                ? String(comp.vendorId)
                : comp.vendor_id != null && comp.vendor_id !== ""
                  ? String(comp.vendor_id)
                  : "";
        updateLine(lineId, {
            componentName: comp.name,
            componentType: comp.type || "",
            description: comp.type || comp.description || "",
            supplier: comp.defaultSupplier || "",
            vendorId: vid,
            componentPresetId:
                comp.componentPresetId != null && comp.componentPresetId !== ""
                    ? String(comp.componentPresetId)
                    : comp.id != null && comp.id !== ""
                      ? String(comp.id)
                      : "",
            qty: Number(comp.defaultQty ?? 1),
            unitCost: Number(comp.defaultUnitCost ?? 0),
            isNewComponent: false,
        });
        setOpenDropdown(null);
        setDropdownSearch("");
    };

    const selectBoxPreset = (lineId, preset) => {
        const presetComponents = preset.components || [];
        const packagingExtendedTotal = arrayHasItems(presetComponents)
            ? Math.round(
                  presetComponents.reduce(
                      (sum, pc) => sum + Number(pc.qty ?? 1) * Number(pc.unitCost ?? 0),
                      0
                  ) * 10000
              ) / 10000
            : 0;
        const packagingLine = {
            id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            componentName: preset.name,
            componentType: "Packaging",
            description: "Packaging",
            supplier: "",
            vendorId: "",
            packagingGroupId: preset.groupId,
            componentPresetId: "",
            qty: arrayHasItems(presetComponents) ? 1 : 0,
            unitCost: packagingExtendedTotal,
            extendedCost: packagingExtendedTotal,
            isNewComponent: false,
        };
        setLines((prev) => {
            const idx = prev.findIndex((l) => l.id === lineId);
            if (idx === -1) return [...prev, packagingLine];
            const next = [...prev];
            next.splice(idx, 1, packagingLine);
            return next;
        });
        setOpenDropdown(null);
        setDropdownSearch("");
    };

    const selectNewComponent = (lineId, name) => {
        updateLine(lineId, {
            componentName: name,
            componentType: "",
            description: "",
            supplier: "",
            vendorId: "",
            componentPresetId: "",
            qty: 1,
            unitCost: 0,
            isNewComponent: true,
        });
        setOpenDropdown(null);
        setDropdownSearch("");
    };

    const addLine = () => setLines((prev) => [...prev, blankLine()]);
    const removeLine = (id) => setLines((prev) => prev.filter((l) => l.id !== id));

    const totalCost = lines.reduce((sum, l) => sum + (l.extendedCost || 0), 0);

    const handleSave = async () => {
        if (!product) return;
        if (!productName.trim()) {
            toast.error("Product name is required", { position: "bottom-right" });
            return;
        }
        const validLines = lines.filter((l) => String(l.componentName || "").trim());
        if (validLines.length === 0) {
            toast.error("Add at least one component", { position: "bottom-right" });
            return;
        }

        const categoryLabel =
            categoryOptions.find((o) => o.value === String(categoryGroupId))?.label ||
            String(product.category || "").trim();
        const subcategoryLabel =
            subcategoryOptions.find((o) => o.value === String(subcategoryVariantId))?.label ||
            String(product.subcategory || "").trim();
        const supplierLabel =
            vendorOptions.find((o) => String(o.value) === String(productVendorId))?.label ||
            String(product.supplier || "").trim();

        const payload = {
            id: product.id,
            supplySku: String(product.supplySku || "").trim().toUpperCase(),
            canonicalName: productName.trim(),
            productName: productName.trim(),
            variantName: variantName.trim(),
            category: categoryLabel,
            subcategory: subcategoryLabel,
            supplier: supplierLabel,
            status: enumInventoryStatuses[statusCode] || statusCode,
            persistCatalogWithBom: true,
            inventoryStatusCode: statusCode,
            category_group_id: categoryGroupId === "" ? null : categoryGroupId,
            subcategory_variant_id: subcategoryVariantId === "" ? null : subcategoryVariantId,
            vendor_id: productVendorId === "" ? null : String(productVendorId),
            leadTimeDays: Number(leadTime || 0),
            totalBomCost: validLines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unitCost || 0), 0),
            componentCount: validLines.length,
            components: validLines.map((l, index) => ({
                id: l.id || `component-${Date.now()}-${index}`,
                componentSku: "",
                componentName: String(l.componentName || "").trim(),
                description: String(l.description || "").trim(),
                componentType: String(l.componentType || "").trim(),
                supplier: String(l.supplier || "").trim(),
                vendorId: l.vendorId ? String(l.vendorId) : "",
                packagingGroupId: l.packagingGroupId ? String(l.packagingGroupId) : "",
                componentPresetId: l.componentPresetId ? String(l.componentPresetId) : "",
                qty: Number(l.qty || 0),
                unitCost: Number(l.unitCost || 0),
                extendedCost: Number(l.qty || 0) * Number(l.unitCost || 0),
            })),
        };

        try {
            await onSave(payload, { newComponents: validLines.filter((l) => l.isNewComponent) });
            onClose();
        } catch {
            /* parent toasts */
        }
    };

    const handleClose = () => onClose();

    if (!product) return null;

    const sku = String(product.supplySku || "").trim();

    const footer = (
        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50/60 px-5 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-gray-500">
                    Total BOM Cost: <span className="font-semibold text-gray-900">{fmtCost(totalCost)}</span>
                    <span className="ml-2 text-xs text-gray-400">
                        ({lines.filter((l) => l.componentName.trim()).length} components)
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="inline-flex h-8 items-center rounded-md border border-gray-200 bg-white px-3 text-xs text-gray-600 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => void handleSave()}
                        disabled={saveBusy || !lines.some((l) => l.componentName.trim())}
                        className="inline-flex relative h-8 items-center gap-1 rounded-md bg-gray-900 px-4 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-40"
                    >
                        {saveBusy && (
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
                                <LoaderSpinner width="14" height="14" colorSpinner="white" border="2" />
                            </div>
                        )}
                        <span>Save Changes</span>
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <Drawer
            isOpen={isOpen}
            onClose={handleClose}
            width="672px"
            bodyClassName="p-0 flex flex-col min-h-0"
            footer={footer}
            headerContent={
                <div className="pr-10">
                    <div className="flex items-center gap-2">
                        <SupplySku sku={sku} />
                        <span className="text-xs text-gray-400">Edit BOM</span>
                    </div>
                    <h2 className="mt-1 text-[14px] font-semibold text-gray-900">
                        {product.canonicalName || product.productName || sku}
                    </h2>
                    <p className="mt-0.5 text-sm text-gray-500">
                        Update components, costs, and product details. Changes apply immediately.
                    </p>
                </div>
            }
        >
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <div className="min-h-0 flex-1 overflow-y-auto">
                    {/* Product details */}
                    <div className="border-b border-gray-100 bg-gray-50/40 px-5 py-4">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-gray-400">
                            Product Details
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-500">SKU</label>
                                <SupplySku sku={sku} />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-500">Status</label>
                                <AddDrawerSelect
                                    options={CATALOG_STATUS_OPTIONS}
                                    value={statusCode}
                                    onChange={(v) => setStatusCode(v || ACTIVE)}
                                    noneLabel="Select status"
                                    stackKey={EDIT_BOM_STACK.status}
                                    openStackKey={openDrawerSelectKey}
                                    onStackKeyChange={setOpenDrawerSelectKey}
                                    selectClassname="w-full"
                                    buttonSelectedClassname="!h-8 !text-xs"
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="mb-1 block text-xs font-medium text-gray-500">Product Name *</label>
                                <input
                                    value={productName}
                                    onChange={(e) => setProductName(e.target.value)}
                                    readOnly
                                    className="h-8 w-full rounded-md border border-gray-200 px-2.5 text-sm text-gray-900 outline-none focus:border-gray-400"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-500">Variant / Color</label>
                                <input
                                    value={variantName}
                                    onChange={(e) => setVariantName(e.target.value)}
                                    placeholder="e.g. Black"
                                    readOnly
                                    className="h-8 w-full rounded-md border border-gray-200 px-2.5 text-sm text-gray-900 outline-none focus:border-gray-400"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-500">Category</label>
                                <AddDrawerSelect
                                    options={categoryOptions}
                                    value={categoryGroupId}
                                    onChange={(v) => {
                                        setCategoryGroupId(v || "");
                                        setSubcategoryVariantId("");
                                    }}
                                    noneLabel="Select category…"
                                    stackKey={EDIT_BOM_STACK.category}
                                    openStackKey={openDrawerSelectKey}
                                    onStackKeyChange={setOpenDrawerSelectKey}
                                    selectClassname="w-full"
                                    buttonSelectedClassname="!h-8 !text-xs"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-500">Primary Supplier</label>
                                <AddDrawerSelect
                                    options={vendorOptions}
                                    value={productVendorId}
                                    onChange={(v) => setProductVendorId(v || "")}
                                    noneLabel="Select supplier…"
                                    allLabel="All suppliers"
                                    isLoading={vendorSelectLoading}
                                    disabled={vendorSelectLoading}
                                    stackKey={EDIT_BOM_STACK.primaryVendor}
                                    openStackKey={openDrawerSelectKey}
                                    onStackKeyChange={setOpenDrawerSelectKey}
                                    selectClassname="w-full"
                                    buttonSelectedClassname="!h-8 !text-xs"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-500">Lead time (days)</label>
                                <input
                                    type="number"
                                    min={0}
                                    placeholder="e.g. 60"
                                    value={leadTime}
                                    onChange={(e) => setLeadTime(e.target.value)}
                                    className="h-8 w-full no-arrows-by-input rounded-md border border-gray-200 px-2.5 text-sm text-gray-900 outline-none focus:border-gray-400"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="px-5 py-4" ref={dropdownRef}>
                            <CopyBomPicker
                                products={productBomRows}
                                excludeProductId={product?.id}
                                selectedProductId={copySourceId}
                                onSelect={handleCopySource}
                            />
                            {copySourceId ? (
                                <p className="mb-3 text-xs font-medium text-blue-600">
                                    BOM copied from the selected source. All lines remain editable.
                                </p>
                            ) : null}
                            <div className="mb-3 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                                        Components
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={addLine}
                                    className="inline-flex h-7 items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 text-xs text-gray-600 hover:bg-gray-50"
                                >
                                    <PlusIcon className="h-3 w-3" />
                                    Add Component
                                </button>
                            </div>

                            <div className="space-y-2">
                                {lines.map((line) => (
                                    <div
                                        key={line.id}
                                        className="rounded-md border border-gray-200 bg-white p-2.5 transition-colors hover:border-gray-300"
                                    >
                                        <div
                                            className="grid items-center gap-x-2"
                                            style={{ gridTemplateColumns: "1fr 80px 80px 72px 28px" }}
                                        >
                                            <div className="relative min-w-0">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setOpenDropdown(openDropdown === line.id ? null : line.id);
                                                        setDropdownSearch("");
                                                    }}
                                                    className={`flex h-7 w-full items-center justify-between gap-1 rounded-md border px-2 text-left text-sm transition-colors focus:outline-none focus:ring-1 focus:ring-gray-400`}
                                                >
                                                    <span
                                                        className={
                                                            line.componentName
                                                                ? "truncate text-gray-900"
                                                                : "truncate text-gray-400"
                                                        }
                                                    >
                                                        {line.componentName || "Select component…"}
                                                    </span>
                                                    <ChevronDownIcon className="h-3 w-3 shrink-0 text-gray-400" />
                                                </button>

                                                {openDropdown === line.id ? (
                                                    <div className="absolute left-0 right-0 top-full z-[100] mt-1 overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl">
                                                        <div className="border-b border-gray-100 p-2">
                                                            <input
                                                                autoFocus
                                                                value={dropdownSearch}
                                                                onChange={(e) => setDropdownSearch(e.target.value)}
                                                                placeholder="Search components…"
                                                                className="h-7 w-full rounded-md border border-gray-200 px-2 text-xs outline-none focus:border-gray-400"
                                                            />
                                                        </div>
                                                        <div className="max-h-64 overflow-y-auto">
                                                            {filteredBoxPresets.length > 0 ? (
                                                                <>
                                                                    <div className="flex items-center gap-1.5 border-b border-gray-200 bg-gray-50 px-3 py-1.5">
                                                                        <CubeIcon className="h-3 w-3 text-gray-400" />
                                                                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                                                                            Box Presets
                                                                        </span>
                                                                    </div>
                                                                    {filteredBoxPresets.map((preset) => (
                                                                        <button
                                                                            key={preset.id}
                                                                            type="button"
                                                                            onClick={() => selectBoxPreset(line.id, preset)}
                                                                            className="flex w-full items-start gap-2 border-b border-gray-100 px-3 py-2 text-left transition-colors last:border-0 hover:bg-blue-50"
                                                                        >
                                                                            <div className="min-w-0 flex-1">
                                                                                <span className="block truncate text-sm text-gray-900">
                                                                                    {preset.name}
                                                                                </span>
                                                                                <span className="block truncate text-xs text-gray-400">
                                                                                    {(preset.components || []).length}{" "}
                                                                                    components · $
                                                                                    {Number(preset.totalCost || 0).toFixed(2)}
                                                                                </span>
                                                                            </div>
                                                                            <span className="mt-0.5 shrink-0 text-xs font-medium text-blue-600">
                                                                                Preset
                                                                            </span>
                                                                        </button>
                                                                    ))}
                                                                </>
                                                            ) : null}
                                                            {filteredLibrary.length > 0 ? (
                                                                <>
                                                                    {filteredBoxPresets.length > 0 ? (
                                                                        <div className="border-b border-gray-200 bg-gray-50 px-3 py-1.5">
                                                                            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                                                                                Component Presets
                                                                            </span>
                                                                        </div>
                                                                    ) : null}
                                                                    {filteredLibrary.map((comp) => (
                                                                        <button
                                                                            key={comp.id}
                                                                            type="button"
                                                                            onClick={() => selectLibraryComponent(line.id, comp)}
                                                                            className="flex w-full items-start gap-2 border-b border-gray-100 px-3 py-2 text-left transition-colors last:border-0 hover:bg-gray-50"
                                                                        >
                                                                            <div className="min-w-0 flex-1">
                                                                                <span className="block truncate text-sm text-gray-900">
                                                                                    {comp.name}
                                                                                </span>
                                                                                <span className="block truncate text-xs text-gray-400">
                                                                                    {comp.type || comp.notes || ""}
                                                                                    {Number(comp.defaultUnitCost) > 0
                                                                                        ? ` · $${Number(comp.defaultUnitCost).toFixed(2)}`
                                                                                        : ""}
                                                                                </span>
                                                                            </div>
                                                                        </button>
                                                                    ))}
                                                                </>
                                                            ) : null}
                                                            {filteredBoxPresets.length === 0 &&
                                                            filteredLibrary.length === 0 &&
                                                            !dropdownSearch.trim() ? (
                                                                <div className="px-3 py-3 text-sm text-gray-400">
                                                                    Add components or box presets in Settings.
                                                                </div>
                                                            ) : null}
                                                            {dropdownSearch.trim() && !dropdownSearchMatchesLibrary ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        selectNewComponent(line.id, dropdownSearch.trim())
                                                                    }
                                                                    className="flex w-full items-center gap-2 border-t border-gray-200 px-3 py-2 text-left transition-colors hover:bg-amber-50"
                                                                >
                                                                    <PlusIcon className="h-3 w-3 shrink-0 text-amber-500" />
                                                                    <div>
                                                                        <span className="text-sm text-gray-700">
                                                                            Use &quot;
                                                                            <span className="font-medium text-gray-900">
                                                                                {dropdownSearch.trim()}
                                                                            </span>
                                                                            &quot;
                                                                        </span>
                                                                        <span className="block text-xs text-amber-600">
                                                                            New component
                                                                        </span>
                                                                    </div>
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>
                                            <input
                                                type="number"
                                                min={0}
                                                value={line.qty}
                                                onChange={(e) =>
                                                    updateLine(line.id, { qty: parseFloat(e.target.value) || 0 })
                                                }
                                                className="h-7 rounded-md border border-gray-200 px-2 text-right text-sm outline-none focus:border-gray-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                            />
                                            <input
                                                type="number"
                                                min={0}
                                                step={0.01}
                                                value={line.unitCost}
                                                onChange={(e) =>
                                                    updateLine(line.id, { unitCost: parseFloat(e.target.value) || 0 })
                                                }
                                                className="h-7 rounded-md border border-gray-200 px-2 text-right text-sm outline-none focus:border-gray-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                            />
                                            <span className="text-right text-sm tabular-nums text-gray-700">
                                                {fmtCost(line.extendedCost)}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => removeLine(line.id)}
                                                className="flex items-center justify-center text-gray-300 hover:text-red-400"
                                                title="Remove"
                                            >
                                                <TrashIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </div>

                                        <div className="mt-1.5 grid grid-cols-2 gap-x-2">
                                            <input
                                                value={line.description}
                                                onChange={(e) => updateLine(line.id, { description: e.target.value })}
                                                placeholder="Description / notes"
                                                className="py-0.5 rounded-md border border-gray-100 bg-gray-50 px-2 text-xs text-gray-800 placeholder:text-gray-300 outline-none"
                                            />
                                            <AddDrawerSelect
                                                options={vendorOptions}
                                                value={line.vendorId ?? ""}
                                                onChange={(value) =>
                                                    updateLine(line.id, {
                                                        vendorId:
                                                            value === undefined || value === null ? "" : String(value),
                                                    })
                                                }
                                                noneLabel="Select supplier…"
                                                allLabel="All suppliers"
                                                isLoading={vendorSelectLoading}
                                                disabled={vendorSelectLoading}
                                                stackKey={PRODUCT_BOM_SUPPLIER_STACK(line.id)}
                                                openStackKey={openDrawerSelectKey}
                                                onStackKeyChange={setOpenDrawerSelectKey}
                                                selectClassname="w-full"
                                                buttonSelectedClassname="!py-0.5 !bg-gray-50 !border-gray-100"
                                            />
                                        </div>
                                    </div>
                                ))}

                                {lines.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-gray-200 py-8 text-gray-300">
                                        <span className="text-sm">No components</span>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                </div>
            </div>
        </Drawer>
    );
}
